import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Search, Plus, Trash2, Users as UsersIcon, Loader2, Shield, Edit, Building2 } from 'lucide-react';
import { menuItems } from '../../components/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = () => ({
    first_name: '', last_name: '', email: '', password: '',
    role: 'manager',
    assigned_branches: [],
    assigned_programs: [],
    sidebar_hidden_paths: [],
});

const roleColors = { super_admin: 'bg-[#7C3AED]', manager: 'bg-[#064E3B]', branch_manager: 'bg-[#0D9488]', teacher: 'bg-[#D97706]', parent: 'bg-[#2563EB]' };

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null); // null = create, else the user being edited
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState(emptyForm());

    useEffect(() => { fetchUsers(); fetchPrograms(); }, []);

    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
            setUsers(res.data);
        } catch { toast.error('Failed to load users'); }
        finally { setLoading(false); }
    };
    const fetchPrograms = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/programs`, { withCredentials: true });
            setPrograms(res.data || []);
        } catch { /* silent */ }
    };

    const branches = useMemo(() => Array.from(new Set(programs.map(p => p.location).filter(Boolean))).sort(), [programs]);
    const branchManagerMenu = menuItems.branch_manager || [];

    const openCreate = () => {
        setEditingUser(null);
        setFormData(emptyForm());
        setDialogOpen(true);
    };
    const openEdit = (u) => {
        setEditingUser(u);
        setFormData({
            first_name: u.first_name || '',
            last_name: u.last_name || '',
            email: u.email || '',
            password: '',
            role: u.role || 'manager',
            assigned_branches: u.assigned_branches || [],
            assigned_programs: u.assigned_programs || [],
            sidebar_hidden_paths: u.sidebar_hidden_paths || [],
        });
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            if (editingUser) {
                // Update role + scope + sidebar via /role endpoint
                await axios.put(`${API_URL}/api/users/${editingUser.id}/role`, {
                    role: formData.role,
                    assigned_programs: formData.assigned_programs,
                    assigned_branches: formData.assigned_branches,
                    sidebar_hidden_paths: formData.sidebar_hidden_paths,
                }, { withCredentials: true });
                toast.success('User updated');
            } else {
                await axios.post(`${API_URL}/api/users`, formData, { withCredentials: true });
                toast.success('User created');
            }
            setDialogOpen(false);
            fetchUsers();
            setFormData(emptyForm());
            setEditingUser(null);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this user?')) return;
        try { await axios.delete(`${API_URL}/api/users/${id}`, { withCredentials: true }); toast.success('User deleted'); fetchUsers(); }
        catch { toast.error('Failed to delete'); }
    };

    const toggleInList = (list, value) => list.includes(value) ? list.filter(x => x !== value) : [...list, value];

    const filtered = users.filter(u => {
        const matchesSearch = u.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) || u.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) || u.email?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesRole = roleFilter === 'all' || u.role === roleFilter;
        return matchesSearch && matchesRole;
    });

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div></div></DashboardLayout>;

    const roleLabel = (r) => (r || '').replace('_', ' ');
    const isBranchManagerForm = formData.role === 'branch_manager';

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="users-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div><h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Users</h1><p className="text-[#4B5563]">Manage system accounts</p></div>
                    <div className="flex gap-3">
                        <Badge variant="secondary"><UsersIcon className="h-4 w-4 mr-1" />{users.length} users</Badge>
                        <Button onClick={openCreate} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="create-user-btn"><Plus className="h-4 w-4 mr-2" />Create User</Button>
                    </div>
                </div>

                <Card className="card-surface"><CardContent className="p-4 flex gap-4">
                    <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" /><Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" /></div>
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                        <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Roles</SelectItem>
                            <SelectItem value="super_admin">Super Admin</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="branch_manager">Branch Manager</SelectItem>
                            <SelectItem value="teacher">Teacher</SelectItem>
                            <SelectItem value="parent">Parent</SelectItem>
                        </SelectContent>
                    </Select>
                </CardContent></Card>

                <Card className="card-surface"><CardContent className="p-0">
                    {filtered.length > 0 ? (
                        <Table>
                            <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Scope</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                            <TableBody>{filtered.map(u => (
                                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                                    <TableCell><div className="flex items-center space-x-3"><div className="h-10 w-10 rounded-full bg-[#064E3B] flex items-center justify-center text-white font-medium">{u.first_name?.[0]}{u.last_name?.[0]}</div><p className="font-medium">{u.first_name} {u.last_name}</p></div></TableCell>
                                    <TableCell>{u.email}</TableCell>
                                    <TableCell><Badge className={`${roleColors[u.role] || 'bg-gray-500'} text-white capitalize`}>{roleLabel(u.role)}</Badge></TableCell>
                                    <TableCell className="text-xs text-[#4B5563]">
                                        {u.role === 'branch_manager' ? (
                                            <div className="space-y-0.5">
                                                {(u.assigned_branches || []).length > 0 && <div className="flex items-center gap-1"><Building2 className="h-3 w-3 text-[#0D9488]" /><span className="font-medium">{u.assigned_branches.join(', ')}</span></div>}
                                                {(u.assigned_programs || []).length > 0 && <div className="text-[#6B7280]">+{u.assigned_programs.length} program(s)</div>}
                                                {(u.assigned_branches || []).length === 0 && (u.assigned_programs || []).length === 0 && <span className="italic text-[#DC2626]">No scope assigned</span>}
                                            </div>
                                        ) : <span className="italic text-[#9CA3AF]">—</span>}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                            {u.role !== 'super_admin' && u.role !== 'parent' && u.role !== 'teacher' && (
                                                <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Edit className="h-4 w-4" /></Button>
                                            )}
                                            {u.role !== 'super_admin' && <Button size="sm" variant="ghost" className="text-[#EF4444]" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}</TableBody>
                        </Table>
                    ) : <div className="p-12 text-center text-[#4B5563]">No users found</div>}
                </CardContent></Card>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle className="font-['Outfit'] flex items-center gap-2"><Shield className="h-5 w-5 text-[#064E3B]" />{editingUser ? 'Edit User' : 'Create User Account'}</DialogTitle></DialogHeader>
                        <form onSubmit={handleSave} className="space-y-4">
                            <Tabs defaultValue="basic" className="w-full">
                                <TabsList className={`grid w-full ${isBranchManagerForm ? 'grid-cols-3' : 'grid-cols-1'}`}>
                                    <TabsTrigger value="basic" data-testid="tab-user-basic">Basic</TabsTrigger>
                                    {isBranchManagerForm && <TabsTrigger value="scope" data-testid="tab-user-scope">Scope</TabsTrigger>}
                                    {isBranchManagerForm && <TabsTrigger value="menu" data-testid="tab-user-menu">Sidebar Menu</TabsTrigger>}
                                </TabsList>
                                <TabsContent value="basic" className="space-y-4 mt-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2"><Label>First Name</Label><Input value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} required disabled={!!editingUser} data-testid="user-first-name" /></div>
                                        <div className="space-y-2"><Label>Last Name</Label><Input value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} required disabled={!!editingUser} data-testid="user-last-name" /></div>
                                    </div>
                                    <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required disabled={!!editingUser} data-testid="user-email" /></div>
                                    {!editingUser && (
                                        <div className="space-y-2"><Label>Password</Label><Input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} required placeholder="Min 8 characters" data-testid="user-password" /></div>
                                    )}
                                    <div className="space-y-2"><Label>Role</Label>
                                        <Select value={formData.role} onValueChange={v => setFormData({...formData, role: v})}>
                                            <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="manager">Manager</SelectItem>
                                                <SelectItem value="branch_manager">Branch Manager (scoped)</SelectItem>
                                                <SelectItem value="teacher">Teacher</SelectItem>
                                                <SelectItem value="parent">Parent</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </TabsContent>
                                {isBranchManagerForm && (
                                    <TabsContent value="scope" className="space-y-4 mt-4">
                                        <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-3 text-xs text-[#166534]">
                                            Assign one or more branches (all their programs) AND/OR specific programs. The Branch Manager will only see students, guardians, classes and lists in this scope.
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="font-semibold">Assigned Branches</Label>
                                            {branches.length === 0 && <p className="text-xs italic text-[#6B7280]">No branches configured yet — set a Location on your programs first.</p>}
                                            <div className="grid grid-cols-2 gap-2" data-testid="assigned-branches">
                                                {branches.map(b => (
                                                    <label key={b} className="flex items-center gap-2 p-2 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB] cursor-pointer">
                                                        <Checkbox
                                                            checked={formData.assigned_branches.includes(b)}
                                                            onCheckedChange={() => setFormData({...formData, assigned_branches: toggleInList(formData.assigned_branches, b)})}
                                                            data-testid={`branch-checkbox-${b}`}
                                                        />
                                                        <span className="text-sm">{b}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="font-semibold">Additional Programs</Label>
                                            <div className="grid grid-cols-1 gap-2 max-h-56 overflow-y-auto border rounded p-2" data-testid="assigned-programs">
                                                {programs.map(p => (
                                                    <label key={p.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-[#F9FAFB] cursor-pointer">
                                                        <Checkbox
                                                            checked={formData.assigned_programs.includes(p.id)}
                                                            onCheckedChange={() => setFormData({...formData, assigned_programs: toggleInList(formData.assigned_programs, p.id)})}
                                                            data-testid={`program-checkbox-${p.id}`}
                                                        />
                                                        <span className="text-sm">{p.name} <span className="text-xs text-[#6B7280]">({p.location || 'no location'})</span></span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </TabsContent>
                                )}
                                {isBranchManagerForm && (
                                    <TabsContent value="menu" className="space-y-4 mt-4">
                                        <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-3 text-xs text-[#1E40AF]">
                                            Check the sidebar items this Branch Manager should NOT see. Unchecked items appear in their menu.
                                        </div>
                                        <div className="grid grid-cols-2 gap-2" data-testid="sidebar-menu-list">
                                            {branchManagerMenu.map(item => (
                                                <label key={item.path} className="flex items-center gap-2 p-2 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB] cursor-pointer">
                                                    <Checkbox
                                                        checked={formData.sidebar_hidden_paths.includes(item.path)}
                                                        onCheckedChange={() => setFormData({...formData, sidebar_hidden_paths: toggleInList(formData.sidebar_hidden_paths, item.path)})}
                                                        data-testid={`sidebar-hidden-${item.path.replace(/\//g, '-')}`}
                                                    />
                                                    <span className="text-sm">Hide <span className="font-medium">{item.label}</span></span>
                                                </label>
                                            ))}
                                        </div>
                                    </TabsContent>
                                )}
                            </Tabs>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="save-user-btn">
                                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                    {editingUser ? 'Update' : 'Create'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default UsersPage;
